import { mapActions, mapGetters, mapMutations } from 'vuex';
import _ from 'lodash';
import * as R from 'ramda';

import iframeMixin from '@/mixins/iframe';
import { useSecondPanel } from '@/composable/useSecondPanel';
import { useFieldLabels } from '@/composable/useFieldLabels';

import RingLoader from '@components/Common/RingLoader';

import * as S from '../options/styles';

import { isValueExist } from '@/utils/utils';
import { IFRAME_EVENT_MESSAGE } from '@consts';
import { getSelectedChannels } from '..';
import { FEATURE_TYPES } from '../const';
import { pluckFieldKey } from './utils';
import { TABLE_METRIC } from '..';
import { sortLongString } from '@/utils/dashboard';

/**
 * 두 배열이 순서에 관계없이 같은 원소를 가지는지 비교
 * assetIds / breakdowns 변경 감지용 (순서만 다른 경우 sync 불필요)
 */
const isSameArrayUnordered = (a, b) => {
    if (!Array.isArray(a) || !Array.isArray(b)) return a === b;
    if (a.length !== b.length) return false;
    const sortedA = sortLongString(a);
    const sortedB = sortLongString(b);
    return sortedA.every((v, i) => v === sortedB[i]);
};

const createReactOptionMixin = (fieldType, selectMode) => {
    return {
        mixins: [iframeMixin],
        setup({ panelKey }) {
            const {
                isSecondPanelOpen,
                secondPanelVisibility$,
                closeSecondPanel,
                toggleSecondPanel,
            } = useSecondPanel({ key: panelKey });

            const { getFieldStatus, getFieldName, fieldLabelsLoading } =
                useFieldLabels(fieldType);

            return {
                isSecondPanelOpen,
                secondPanelVisibility$,
                closeSecondPanel,
                toggleSecondPanel,
                getFieldStatus,
                getFieldName,
                fieldLabelsLoading,
            };
        },
        props: {
            disabled: {
                type: Boolean,
                default: false,
            },
            panelLoading: {
                type: Boolean,
                default: false,
            },
            widgetType: String,
            graphOptions: {
                type: Object,
                default() {
                    return {};
                },
            },
            selectedSource: {
                type: Array,
                default() {
                    return [];
                },
            },
            currentBreakdownIds: {
                type: Array,
                default() {
                    return [];
                },
            },
            isAnalytic: {
                type: Boolean,
                default: false,
            },
            teamspaceId: Number,
            currentViewId: Number | String,
            isTemplateView: Boolean,
            isForDatastream: {
                type: Boolean,
                default: false,
            },
            hideCustomTab: {
                type: Boolean,
                default: false,
            },
            datasources: {
                type: Array,
                default() {
                    return [];
                },
            },
            currentListByType: {
                type: Function,
                default: R.always([]),
            },
            comparisonByDate: {
                type: Boolean,
                default: false,
            },
            columnDimensionIds: {
                type: Array,
                default() {
                    return [];
                },
            },
        },
        watch: {
            isSecondPanelOpen(isOpen) {
                if (isOpen) {
                    this._startSyncWatchers();
                } else {
                    this._stopSyncWatchers();
                    // 같은 타입의 다른 패널로 전환 시 close skip
                    // openReactPanel()이 동기적으로 먼저 실행되므로 store에는
                    // 이미 새 패널의 panelKey가 저장된 상태 → 불일치 시 close 불필요
                    const storeKey =
                        fieldType === 'metric'
                            ? this.$store.state.newDashboard?.metricPanelData
                                  ?.panelKey
                            : this.$store.state.newDashboard?.breakdownPanelData
                                  ?.panelKey;
                    if (storeKey && storeKey !== this.panelKey) return;
                    this.closeReactPanel();
                }
            },
        },
        computed: {
            ...mapGetters('newDashboard', ['currentView']),
            assetIds() {
                return this.selectedSource.map((v) => v.assetId);
            },
            selectedChannels() {
                return getSelectedChannels(this.selectedSource);
            },
            fieldWidgetType() {
                return this.isAnalytic ? 'normal' : 'marketing';
            },
            selectedFields() {
                return pluckFieldKey(this.currentList);
            },
            isComparisonByDate() {
                return (
                    this.comparisonByDate ||
                    isValueExist(this.columnDimensionIds)
                );
            },
        },
        created() {
            // methods는 prototype 공유이므로 인스턴스별 debounce 함수로 교체
            this.syncApiParams = _.debounce(this.syncApiParams, 500);
            this.syncSelectedFields = _.debounce(this.syncSelectedFields, 500);

            window.addEventListener('message', this._onReactPanelClose);
        },
        beforeDestroy() {
            this._stopSyncWatchers();
            window.removeEventListener('message', this._onReactPanelClose);
        },
        methods: {
            ...mapActions('iframe', ['postIframeMessage']),
            ...mapMutations('newDashboard', [
                'setMetricPanelData',
                'setBreakdownPanelData',
            ]),
            // React에서 패널을 닫을 때(mask 클릭 등) Vue의 isSecondPanelOpen 동기화
            _onReactPanelClose(event) {
                const closeEventType =
                    fieldType === 'metric'
                        ? IFRAME_EVENT_MESSAGE.TOGGLE_METRIC_PANEL
                        : IFRAME_EVENT_MESSAGE.TOGGLE_BREAKDOWN_PANEL;
                const msg = event.data;
                if (msg?.type !== closeEventType) return;
                if (msg?.origin !== 'REACT') return;
                if (msg?.data !== null) return;
                this.closeSecondPanel();
            },
            // 패널이 열려 있을 때만 watch — 닫힌 상태에서 불필요한 감지 방지
            _startSyncWatchers() {
                this._syncUnwatchers = [
                    // API 파라미터 변경 감지 (datasource, breakdown, widgetType)
                    this.$watch('assetIds', (newVal, oldVal) => {
                        if (isSameArrayUnordered(newVal, oldVal)) return;
                        this.syncApiParams();
                    }),
                    this.$watch('currentBreakdownIds', (newVal, oldVal) => {
                        if (isSameArrayUnordered(newVal, oldVal)) return;
                        this.syncApiParams();
                    }),
                    this.$watch('fieldWidgetType', () => {
                        this.syncApiParams();
                    }),
                    this.$watch('datasources', () => {
                        this.syncApiParams();
                    }),

                    // 선택된 field 변경 감지
                    this.$watch('selectedFields', (newVal, oldVal) => {
                        if (R.equals(newVal, oldVal)) return;
                        this.syncSelectedFields();
                    }),
                ];
            },
            _stopSyncWatchers() {
                this._syncUnwatchers?.forEach((unwatch) => unwatch());
                this._syncUnwatchers = [];
                this.syncApiParams.cancel?.();
                this.syncSelectedFields.cancel?.();
            },
            syncApiParams() {
                const type =
                    fieldType === 'metric'
                        ? IFRAME_EVENT_MESSAGE.SYNC_METRIC_PANEL_API_PARAMS
                        : IFRAME_EVENT_MESSAGE.SYNC_BREAKDOWN_PANEL_API_PARAMS;

                this.postIframeMessage({
                    origin: 'VUE',
                    type,
                    data: {
                        panelKey: this.panelKey,
                        assetIds: this.assetIds,
                        breakdowns: this.currentBreakdownIds,
                        widgetType: this.fieldWidgetType,
                        selectedChannels: this.selectedChannels,
                        ...(fieldType === 'breakdown' && {
                            datasources: this.datasources,
                        }),
                    },
                });
            },
            syncSelectedFields() {
                const type =
                    fieldType === 'metric'
                        ? IFRAME_EVENT_MESSAGE.UPDATE_METRIC_PANEL
                        : IFRAME_EVENT_MESSAGE.UPDATE_BREAKDOWN_PANEL;

                this.postIframeMessage({
                    origin: 'VUE',
                    type,
                    data: {
                        panelKey: this.panelKey,
                        selectedFields: this.selectedFields,
                    },
                });
            },
            closeReactPanel() {
                const closeEventType =
                    fieldType === 'metric'
                        ? IFRAME_EVENT_MESSAGE.TOGGLE_METRIC_PANEL
                        : IFRAME_EVENT_MESSAGE.TOGGLE_BREAKDOWN_PANEL;
                this.postIframeMessage({
                    origin: 'VUE',
                    type: closeEventType,
                    data: null,
                });
            },
            openReactPanel() {
                const {
                    assetIds,
                    currentBreakdownIds: breakdowns,
                    fieldWidgetType,
                    teamspaceId,
                    currentViewId: viewId,

                    panelKey,
                    selectedChannels,
                    selectedFields,
                    limitCount,
                    title,
                    mainGuide,
                    subGuide,
                } = this;

                const commonData = {
                    assetIds,
                    breakdowns,
                    widgetType: fieldWidgetType,
                    teamspaceId,
                    viewId,
                    panelKey,
                    selectedChannels,
                    selectedFields,
                    selectMode,
                    limitCount,
                    title,
                    mainGuide,
                    subGuide,

                    filterFeatureType: FEATURE_TYPES.WIDGET_METRIC,
                    filterWidgetType: this.widgetType,
                    filterGraphType: this.graphOptions?.selectedGraphType,

                    currentView: this.currentView,
                    isTemplateView: this.isTemplateView,

                    isForDatastream: this.isForDatastream,
                    hideCustomTab: this.hideCustomTab,

                    lockedFields: this.fixedList ?? [],
                    skipBreakdownOrderRule: !!(this.isFromQuickControl),
                };

                switch (fieldType) {
                    case 'metric': {
                        const data = {
                            ...commonData,
                            canEditCustomMetric: this.canEditCustomMetric,
                        };
                        this.setMetricPanelData(data);
                        this.postIframeMessage({
                            origin: 'VUE',
                            type: IFRAME_EVENT_MESSAGE.TOGGLE_METRIC_PANEL,
                            data,
                        });

                        break;
                    }
                    case 'breakdown': {
                        const data = {
                            ...commonData,
                            canEditCustomBreakdown: this.canEditCustomBreakdown,
                            selectedMetrics: pluckFieldKey(
                                this.currentListByType(TABLE_METRIC)
                            ),
                            isComparisonByDate: this.isComparisonByDate,
                            datasources: this.datasources,
                        };
                        this.setBreakdownPanelData(data);
                        this.postIframeMessage({
                            origin: 'VUE',
                            type: IFRAME_EVENT_MESSAGE.TOGGLE_BREAKDOWN_PANEL,
                            data,
                        });
                        break;
                    }
                }
            },
            wrapOptions({
                children,
                title,
                icon,
                headerContent,
                loading = false,
            }) {
                const { isSecondPanelOpen, disabled } = this;

                return (
                    <S.ListWrapper
                        isHoverPointer={true}
                        onClick={() => {
                            if (disabled) return;

                            if (!isSecondPanelOpen) {
                                this.openReactPanel();
                            }

                            this.toggleSecondPanel();
                        }}
                        disabled={disabled}
                        class="option-wrapper"
                    >
                        <S.TitleLabel
                            class="option-title"
                            hideBorder={!isValueExist(children)}
                        >
                            <S.IconWrapper class="option-title-text">
                                {icon}
                                {title}
                            </S.IconWrapper>
                            {isValueExist(headerContent) && headerContent}

                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                }}
                            >
                                {loading && <RingLoader size={16} />}
                                <S.EditButton highlighted={!isSecondPanelOpen}>
                                    {this.$t('COMMON.edit')}
                                </S.EditButton>
                            </div>
                        </S.TitleLabel>
                        {isValueExist(children) && children}
                    </S.ListWrapper>
                );
            },
            renderNoList(msg) {
                return <S.NoListSpan>+ {msg}</S.NoListSpan>;
            },
        },
    };
};

export const SingleMetricOptionMixin = createReactOptionMixin(
    'metric',
    'single'
);
export const MultiMetricOptionMixin = createReactOptionMixin('metric', 'multi');
export const SingleBreakdownOptionMixin = createReactOptionMixin(
    'breakdown',
    'single'
);
export const MultiBreakdownOptionMixin = createReactOptionMixin(
    'breakdown',
    'multi'
);
